
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the URL hash and log it for debugging
        const hash = window.location.hash;
        console.log("Auth callback URL hash:", hash);
        
        // Check if we're in a development environment
        const isDev = window.location.hostname === 'localhost' || 
                     window.location.hostname.includes('lovableproject.com');
        
        if (isDev) {
          console.log("Development environment detected, handling auth specially");
        }
        
        // Process the callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error in auth callback:", error);
          setError(`Authentication error: ${error.message}. Please check Supabase URL configuration.`);
          return;
        }
        
        if (data?.session) {
          console.log("Authentication successful, redirecting to home");
          navigate("/");
        } else {
          console.error("No session found in auth callback");
          setError("No session found. Authentication failed. Check Supabase redirect URLs.");
        }
      } catch (err) {
        console.error("Unexpected error in auth callback:", err);
        setError("An unexpected error occurred during authentication.");
      }
    };
    
    handleAuthCallback();
  }, [navigate]);
  
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center max-w-md w-full p-4">
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription>
              {error}
              <p className="mt-2 text-sm">
                If you're editing this site on Lovable, please make sure that:
              </p>
              <ul className="mt-2 text-sm text-left list-disc pl-5">
                <li>Your Supabase project URL configuration includes your Lovable project URL</li>
                <li>Add "{window.location.origin}" to the allowed redirect URLs in Supabase</li>
                <li>Add "{window.location.origin}/auth/callback" to the redirect URLs</li>
              </ul>
              <button 
                onClick={() => navigate("/auth")} 
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Return to Login
              </button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin" />
            <p className="mt-4">Completing authentication...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
